/* ++++++++++ --------------- IMPORTS --------------- ++++++++++ */
// libraries
import React from 'react';
import { ResponsiveLine } from '@nivo/line'; // https://nivo.rocks/line/
// material
import { themeV2 } from '@insticator/insticator-ui';
// utils
import {
    CHART_SETTINGS,
    formatNumber,
    formatTooltipDate,
    formatXAxisDates,
    formatYAxis, processDataForAxis
} from '../../../../../utils/charts';
// styles
import injectSheet from 'react-jss';
import styles from './revenuereportchart_styles';



/* ========== ~~~~~~~~~~ REVENUE REPORT CHART ~~~~~~~~~~ ========== */
const RevenueReportChart = injectSheet(styles)((props) => {
    let maxY;

    const DashedSolidLine = ({ series, lineGenerator, xScale, yScale }) => {
        return series.map(({ id, data, color }, index) => (
          <path
            key={id}
            d={lineGenerator(
              data.map((d) => ({
                x: xScale(d.data.x),
                y: yScale(d.data.y)
              }))
            )}
            fill="none"
            stroke={color}
            style={
              index % 2 === 1
                ? {
                    // simulate line will dash stroke when index is odd (even-number element)
                    strokeDasharray: "4, 4",
                    strokeWidth: 1.75
                  }
                : {
                    // simulate line with solid stroke
                    strokeWidth: 1.75
                  }
            }
          />
        ));
      };

    const renderCrosshairCircles = (filteredData, selectedIndex, dataLength, currentY, maxY, height = 181, offset = 18.25) => {
        let points = filteredData.map(formatData => {
            let data = formatData.data[selectedIndex % dataLength];
            let color = formatData.color;
            let diff = currentY - (data ? data.y : 0);
            let calc = (diff / (maxY * 1.5)) * height;
            return { color: color, dist: calc + offset };
        });

        return points.map(point => {
            return (
                <div style={{
                    position: 'absolute',
                    top: `${point.dist}px`,
                    width: '10px',
                    height: '10px',
                    borderRadius: '50%',
                    background: 'rgb(255, 255, 255)',
                    border: `2px solid ${point.color}`
                }} />)
        });
    }

    const renderCustomTooltip = dataSlice => {
        const dataLengthInHalf = Object.keys(props.data[0].data).length / 2;
        const isFirstHalf = dataSlice.point.data.dataIndex < dataLengthInHalf || dataSlice.point.x < dataLengthInHalf

        let currentData = { x: dataSlice.point.data.x, y: dataSlice.point.data.y , serieColor: dataSlice.point.serieColor, originalDate: dataSlice.point.data.originalDate, startDate: dataSlice.point.data.startDate, endDate: dataSlice.point.data.endDate};
        let compareData = dataSlice.point.serieId === 'CompareTo' ? formattedData[0].data[dataSlice.point.index - dateLength] : formattedData[1].data[dataSlice.point.index];

        // this needs to be done at this point before the swap occurs
        let renderPoint = renderCrosshairCircles(formattedData.filter(value => value.id !== dataSlice.point.serieId), dataSlice.point.index, dateLength, currentData.y, maxY);
        if (dataSlice.point.serieId === 'CompareTo') {
            const compareDataCopy = compareData;
            compareData = currentData;
            currentData = compareDataCopy;
        }
    
        // width of tooltip box
        let rectLength;
        if(props.period === 'daily') rectLength = 110;
        else if(props.period === 'weekly') rectLength = 220;
        else if(props.period === 'monthly') rectLength = 100;
        else if(props.period === 'quarterly') rectLength = 140; 
        else rectLength = 120;

        // width of date value
        let valueLength = 100;
        if(currentData && compareData)
            valueLength = currentData.y > compareData.y ? formatNumber(currentData.y, 'dollar').length * 11 : formatNumber(compareData.y, 'dollar').length * 11;
        else if(currentData && !compareData)
            valueLength = formatNumber(currentData.y, 'dollar').length * 11;
        else if(!currentData && compareData)
            valueLength = formatNumber(compareData.y, 'dollar').length * 11;


        // hover text 
        const currentDateHoverText = currentData ? formatTooltipDate(currentData.originalDate, props.period, currentData.startDate, currentData.endDate) : '';
        const compareDateHoverText = compareData ? formatTooltipDate(compareData.originalDate, props.period, compareData.startDate, compareData.endDate) : '';

        return (
            <React.Fragment>
            <div style={{ position: 'relative', top: '18.25px', width: '10px', height: '10px', borderRadius: '50%', background: 'rgb(255, 255, 255)', border: `2px solid ${dataSlice.point.serieColor}`}}>
                <div
                    style={{
                        background: '#FFFFFF',
                        border: '1px solid rgb(236, 240, 247)',
                        borderRadius: '0px',
                        color: '#062B75',
                        marginLeft: '15px',
                        padding: '5px',
                        display: 'block',
                        minWidth: `${rectLength + 30 + valueLength + 30}px`,
                        height: compareData && compareData.y > 0 ? '60px' : '40px',
                        boxShadow: '0px 2px 5px 0px rgba(36, 78, 185, 0.1), 0px 0px 5px 0px rgba(172, 217, 251, 0.2)',
                        fontSize: '16px',
                        position: 'relative',
                        top: props.period === 'weekly' ? '-50px' : '-20px',
                        left: (isFirstHalf ? 0 : '-240px')
                    }}
                >
                    <div style={{display: 'flex', height: '25px', paddingTop: '4px'}}>
                        <div style={{height: '15px', width: '15px', background: currentData && currentData.serieColor ? currentData.serieColor : themeV2.color.primary.main, margin: '3px 6px 0px 4px'}}></div>
                        <span style={{marginRight: '6px', fontWeight: '500'}}>
                            { currentData ? currentDateHoverText : '' }  
                            { compareData && !currentData ? compareDateHoverText : ''}
                        </span>
                        <span style={{fontWeight: '600'}}>{currentData ? formatNumber(currentData.y, 'dollar') : 0}</span>
                    </div>
                    { compareData && compareData.y > 0 &&
                    <div style={{display: 'flex', height: '25px'}}>
                        <div style={{height: '15px', width: '15px', borderStyle: 'dotted', borderColor: compareData && compareData.serieColor ? compareData.serieColor : '', margin: '3px 6px 0px 4px'}}></div>
                        <span style={{marginRight: '6px', fontWeight: '500'}}>
                            { compareData ? compareDateHoverText : ''}
                            { currentData && !compareData ? currentDateHoverText : ''}
                        </span>
                        <span style={{fontWeight: '600'}}>{compareData ? formatNumber(compareData.y, 'dollar') : 0}</span>
                    </div>
                    }
                </div>
            </div>
            {props.compareTo === 'YoY' ? renderPoint : ''}
        </React.Fragment>

        )
    }

    const dateLength = !!props.data[0] ? Object.keys(props.data[0].data).length : null;

    const formatDataForVisualization = (data) => {
        const dateRange = Object.keys(data[0].data);
        return data.map(obj => {
            return {
                id: obj.id,
                color: obj.id === 'Current' ? themeV2.color.primary.main : '#9baac8',
                data: Object.keys(obj.data).map((date, index) => {
                    let revenue = obj.data[date].revenue;
                    maxY = !maxY || maxY < revenue ? revenue : maxY;
                    // To allow duplicate x-axis labels // TODO: Refactor this
                    let spaces = '';
                    for(var i = 0; i < index; i++) spaces += ' ';
                    return {
                            x: obj.id === 'Current' ? formatXAxisDates(date, props.period, dateLength)?.concat(spaces) : formatXAxisDates(dateRange[index], props.period, dateLength)?.concat(spaces), 
                            y: revenue, 
                            originalDate: date,
                            startDate: obj.data[date].startDate ? obj.data[date].startDate  : null,
                            endDate: obj.data[date].endDate ? obj.data[date].endDate : null,
                            dataIndex: index
                    };
                })
            };
        });
    }

    const formattedData = props.status === 'fulfilled'? formatDataForVisualization(props.data) : null;

    const lineProps = dateLength && props.period.toLowerCase() === 'daily' ?
        CHART_SETTINGS.linePropsForTimeScale(dateLength) :
        CHART_SETTINGS.linePropsDefualt(dateLength)

    return (
        <div className={`revenuereportchart ${props.classes.revenuereportchart}`}>
            <ResponsiveLine
                {...lineProps}
                data={props.period.toLowerCase() === 'daily' ? processDataForAxis(formattedData) : formattedData}
                yScale={{
                    type: "linear",
                    stacked: false,
                    min: 0,
                    max: maxY && !Number.isNaN(maxY) ? (maxY * 1.5) : 'auto'
                }}
                axisLeft={{
                    orient: 'left',
                    tickSize: 0,
                    tickPadding: 20,
                    tickValues: 4,
                    tickRotation: 0,
                    legend: null,
                    format: value => formatYAxis(value, 'dollar')
                }}
                tooltip={renderCustomTooltip}
                enableGridX={false}
                enableGridY={true}
                gridYValues={4}
                colors={d => d.color}
                lineWidth={2}
                enablePoints={props.enablePoints}
                pointSize={8}
                pointColor={{ theme: 'background' }}
                pointBorderWidth={2}
                pointBorderColor={{ from: 'serieColor' }}
                animate={true}
                motionStiffness={90}
                motionDamping={15}
                useMesh={true}
                enableCrosshair={true}
                crosshairType={'x'}
                enableArea={props.compareTo === 'PoP' ? true : false}
                areaOpacity={1}
                layers={['grid', 'markers', 'axes', 'areas', 'crosshair', 'points', 'slices', 'mesh', 'legends', DashedSolidLine] }
                theme={{
                    crosshair: {
                        line: {
                            stroke: 'rgba(6, 43, 117, 0.5)',
                            strokeWidth: 1,
                            strokeOpacity: 0.75,
                            strokeDasharray: ''
                        }
                    },
                    grid: {
                        line: {
                            stroke: '#eef3ff',
                            strokeWidth: 1
                        }
                    },
                    axis: {
                        ticks: {
                            text: {
                                stroke: 'rgba(6,43,117,0.5)',
                                strokeWidth: 0.1,
                                fontSize: '12px',
                                fontFamily: 'Work Sans',
                                fill: 'rgba(6,43,117,0.5)'
                            }
                        }
                    }
                }}
            />
        </div>
    );
});


/* ++++++++++ --------------- EXPORTS --------------- ++++++++++ */
export default RevenueReportChart;

